import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import MainLayout from '@/components/layout/MainLayout';
import { SEOHead } from '@/components/seo/SEOHead';
import LocalizedLink from '@/i18n/LocalizedLink';
import { useLang } from '@/i18n/useLocalizedPath';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Download, ChevronRight, Sparkles, Loader2, FileText, Mail, Check, BookOpen, Calculator, MapPin } from 'lucide-react';
import { BoostBanner } from '@/components/boost/QuickShareButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function InsightsPage() {
  const { t } = useTranslation();
  const lang = useLang();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [lang]);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/reports?lang=${lang}`);
      setReports(response.data.reports || []);
    } catch (err) {
      console.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail) return;
    
    setSubscribing(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/insights/subscribe`, {
        email: subscribeEmail
      });
      setSubscribed(true);
    } catch (err) {
      console.error('Failed to subscribe');
    } finally {
      setSubscribing(false);
    }
  };

  const featuredReport = reports[0];
  const otherReports = reports.slice(1);

  return (
    <MainLayout>
      <SEOHead
        title="Industry Insights & Trend Reports | Jigsaw Conferences"
        description="AI-powered analysis of the UK business events landscape. Get quarterly trend reports on event volumes, venues, and industry forecasts."
        url="https://events.jigsawconferences.co.uk/insights"
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-purple-900 via-purple-800 to-orange-600 text-white py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-8 h-8" />
            <span className="text-purple-200 font-medium">{t('insights.label')}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            {t('insights.heroTitle')}
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl">
            {t('insights.heroSubtitle')}
          </p>
        </div>
      </section>

      {/* Downloadable Resources Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{t('insights.resourcesTitle')}</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t('insights.resourcesSubtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Event Planning Checklist */}
            <Card className="hover:shadow-lg transition-shadow border-2 hover:border-purple-200">
              <CardHeader className="pb-3">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-3">
                  <BookOpen className="w-6 h-6 text-purple-600" />
                </div>
                <CardTitle className="text-lg">{t('insights.checklistTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  {t('insights.checklistDesc')}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                  <Badge variant="secondary">PDF</Badge>
                  <span>4 {t('insights.pagesLabel')}</span>
                  <span>•</span>
                  <span>{t('insights.edition')}</span>
                </div>
                <a href="/downloads/event-planning-checklist-2025.pdf" download>
                  <Button className="w-full" variant="outline">
                    <Download className="w-4 h-4 mr-2" /> {t('insights.downloadFree')}
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* Venue Selection Guide */}
            <Card className="hover:shadow-lg transition-shadow border-2 hover:border-purple-200">
              <CardHeader className="pb-3">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-3">
                  <MapPin className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle className="text-lg">{t('insights.venueGuideTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  {t('insights.venueGuideDesc')}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                  <Badge variant="secondary">PDF</Badge>
                  <span>3 {t('insights.pagesLabel')}</span>
                  <span>•</span>
                  <span>{t('insights.edition')}</span>
                </div>
                <a href="/downloads/venue-selection-guide-2025.pdf" download>
                  <Button className="w-full" variant="outline">
                    <Download className="w-4 h-4 mr-2" /> {t('insights.downloadFree')}
                  </Button>
                </a>
              </CardContent>
            </Card>

            {/* ROI Calculator Guide */}
            <Card className="hover:shadow-lg transition-shadow border-2 hover:border-purple-200">
              <CardHeader className="pb-3">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-3">
                  <Calculator className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle className="text-lg">{t('insights.roiTitle')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  {t('insights.roiDesc')}
                </p>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                  <Badge variant="secondary">PDF</Badge>
                  <span>3 {t('insights.pagesLabel')}</span>
                  <span>•</span>
                  <span>{t('insights.edition')}</span>
                </div>
                <a href="/downloads/event-roi-calculator-guide-2025.pdf" download>
                  <Button className="w-full" variant="outline">
                    <Download className="w-4 h-4 mr-2" /> {t('insights.downloadFree')}
                  </Button>
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('insights.comingSoonTitle')}</h2>
              <p className="text-gray-600 mb-8">{t('insights.comingSoonDesc')}</p>
              
              {/* Subscribe CTA */}
              <Card className="max-w-md mx-auto bg-gradient-to-br from-purple-50 to-orange-50 border-purple-200">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-gray-900 mb-2">{t('insights.getNotified')}</h3>
                  <p className="text-sm text-gray-600 mb-4">{t('insights.getNotifiedDesc')}</p>
                  {subscribed ? (
                    <div className="flex items-center justify-center text-green-600">
                      <Check className="w-5 h-5 mr-2" /> {t('insights.subscribed')}
                    </div>
                  ) : (
                    <form onSubmit={handleSubscribe} className="flex gap-2">
                      <Input
                        type="email"
                        placeholder={t('insights.emailPlaceholder')}
                        value={subscribeEmail}
                        onChange={(e) => setSubscribeEmail(e.target.value)}
                        required
                        className="flex-1"
                      />
                      <Button type="submit" disabled={subscribing}>
                        {subscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : t('insights.subscribe')}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              {/* Featured Report */}
              {featuredReport && (
                <Card className="mb-8 overflow-hidden">
                  <div className="md:flex">
                    <div className="md:w-2/3 p-6 md:p-8">
                      <Badge className="bg-purple-100 text-purple-800 mb-3">
                        <Sparkles className="w-3 h-3 mr-1" /> {t('insights.featuredReport')}
                      </Badge>
                      <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                        {featuredReport.title}
                      </h2>
                      <p className="text-gray-600 mb-4">
                        {featuredReport.content?.executiveSummary?.slice(0, 200)}...
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
                        <span>{t('insights.eventsAnalyzedInline', { count: featuredReport.data?.totalEvents })}</span>
                        <span>·</span>
                        <span>{new Date(featuredReport.publishedAt).toLocaleDateString()}</span>
                      </div>
                      <div className="flex gap-3">
                        <LocalizedLink to={`/insights/${featuredReport.slug}`}>
                          <Button>
                            {t('insights.readFullReport')}
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </LocalizedLink>
                        <LocalizedLink to={`/insights/${featuredReport.slug}#download`}>
                          <Button variant="outline">
                            <Download className="w-4 h-4 mr-2" />
                            {t('insights.getPdf')}
                          </Button>
                        </LocalizedLink>
                      </div>
                    </div>
                    <div className="md:w-1/3 bg-gradient-to-br from-purple-600 to-orange-500 p-6 md:p-8 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="text-5xl font-bold mb-2">{featuredReport.data?.totalEvents}</div>
                        <div className="text-purple-100">{t('insights.eventsAnalyzedLabel')}</div>
                        <div className="text-2xl font-semibold mt-4">{featuredReport.period}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Other Reports Grid */}
              {otherReports.length > 0 && (
                <>
                  <h2 className="text-xl font-bold text-gray-900 mb-4">{t('insights.allReports')}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {otherReports.map((report) => (
                      <LocalizedLink key={report.id} to={`/insights/${report.slug}`}>
                        <Card className="h-full hover:shadow-lg transition-shadow">
                          <CardHeader>
                            <Badge variant="secondary" className="w-fit mb-2">
                              {report.sector}
                            </Badge>
                            <CardTitle className="text-lg">{report.title}</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                              {report.content?.executiveSummary?.slice(0, 120)}...
                            </p>
                            <div className="flex items-center justify-between text-xs text-gray-500">
                              <span>{t('insights.eventsShort', { count: report.data?.totalEvents })}</span>
                              <span>{new Date(report.publishedAt).toLocaleDateString()}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </LocalizedLink>
                    ))}
                  </div>
                </>
              )}

              {/* Subscribe Section */}
              <Card className="bg-gradient-to-r from-purple-50 to-orange-50 border-purple-200">
                <CardContent className="py-8">
                  <div className="max-w-xl mx-auto text-center">
                    <Mail className="w-10 h-10 mx-auto mb-4 text-purple-600" />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {t('insights.getQuarterlyTitle')}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      {t('insights.getQuarterlyDesc')}
                    </p>
                    {subscribed ? (
                      <div className="flex items-center justify-center text-green-600 font-medium">
                        <Check className="w-5 h-5 mr-2" /> {t('insights.subscribedInbox')}
                      </div>
                    ) : (
                      <form onSubmit={handleSubscribe} className="flex gap-2 max-w-md mx-auto">
                        <Input
                          type="email"
                          placeholder={t('insights.emailPlaceholder')}
                          value={subscribeEmail}
                          onChange={(e) => setSubscribeEmail(e.target.value)}
                          required
                          className="flex-1"
                        />
                        <Button type="submit" disabled={subscribing}>
                          {subscribing ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>{t('insights.getQ1')} <ChevronRight className="w-4 h-4 ml-1" /></>
                          )}
                        </Button>
                      </form>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Event Boost CTA */}
              <div className="mt-8">
                <BoostBanner variant="compact" />
              </div>
            </>
          )}
        </div>
      </section>
    </MainLayout>
  );
}
